/**
 * SpineSymbolManager — manages Spine display object instances for the
 * cascade slot grid during play mode.
 *
 * Handles skeleton data loading (via SpineOffscreenRenderer cache),
 * instance pooling, and animation trigger dispatch.
 */

import { Spine, SkeletonData } from '@esotericsoftware/spine-pixi-v8';
import { spineOffscreenRenderer } from '../SpineOffscreenRenderer';
import type {
  CascadeSymbolConfig,
  SymbolSpineConfig,
  SymbolAnimationTrigger,
} from '@core/types/cascade-slot.types';

// ============================================================================
// Types
// ============================================================================

interface PoolEntry {
  spine: Spine;
  skeletonKey: string;
}

// ============================================================================
// SpineSymbolManager
// ============================================================================

export class SpineSymbolManager {
  /** Cached SkeletonData keyed by skeletonPath */
  private skeletonDataCache = new Map<string, SkeletonData>();

  /** Pool of available (not in use) Spine instances per skeleton key */
  private pool = new Map<string, PoolEntry[]>();

  /** Active Spine instances keyed by cell key ("r{row}c{col}") */
  private activeSpines = new Map<string, PoolEntry>();

  /** Symbol configs keyed by symbol ID for quick animation lookup */
  private symbolConfigs = new Map<string, CascadeSymbolConfig>();

  /** Map from cellKey → symbolId (to look up the right config) */
  private cellSymbolIds = new Map<string, string>();

  /** Global time scale multiplier */
  private globalTimeScale = 1.0;

  private disposed = false;

  // ── Lifecycle ───────────────────────────────────────────────────

  /**
   * Preload skeleton data for all spine-mode symbols.
   * Call once when entering play mode.
   */
  async preload(symbols: CascadeSymbolConfig[]): Promise<void> {
    // Index symbol configs
    for (const sym of symbols) {
      this.symbolConfigs.set(sym.id, sym);
    }

    // Collect unique skeleton paths
    const seen = new Set<string>();
    const toLoad: SymbolSpineConfig[] = [];

    for (const sym of symbols) {
      if (sym.displayMode !== 'spine' || !sym.spineConfig) continue;
      const key = sym.spineConfig.skeletonPath;
      if (seen.has(key)) continue;
      seen.add(key);
      toLoad.push(sym.spineConfig);
    }

    // Load skeleton data in parallel
    const results = await Promise.allSettled(
      toLoad.map(async (cfg) => {
        try {
          const skelData = await spineOffscreenRenderer.getSkeletonData(
            cfg.skeletonPath,
            cfg.atlasPath,
            cfg.texturePaths,
          );
          this.skeletonDataCache.set(cfg.skeletonPath, skelData);
        } catch (err) {
          console.warn(
            `[SpineSymbolManager] Failed to load skeleton: ${cfg.skeletonPath}`,
            err,
          );
        }
      }),
    );

    // Log any failures
    for (const result of results) {
      if (result.status === 'rejected') {
        console.warn('[SpineSymbolManager] Preload rejection:', result.reason);
      }
    }
  }

  // ── Spine Instance Management ───────────────────────────────────

  /**
   * Acquire a Spine instance for a grid cell. Returns null if the skeleton
   * data wasn't loaded (preload failed or not a spine symbol).
   */
  acquireSpine(cellKey: string, symbolId: string, config: SymbolSpineConfig): Spine | null {
    if (this.disposed) return null;

    const skelData = this.skeletonDataCache.get(config.skeletonPath);
    if (!skelData) return null;

    // If this cell already has a Spine instance with the same skeleton, reuse it
    const existing = this.activeSpines.get(cellKey);
    if (existing && existing.skeletonKey === config.skeletonPath) {
      this.cellSymbolIds.set(cellKey, symbolId);
      return existing.spine;
    }

    // Release any existing spine on this cell
    if (existing) {
      this.releaseSpine(cellKey);
    }

    // Try to get from pool
    const skeletonKey = config.skeletonPath;
    const poolStack = this.pool.get(skeletonKey);
    let entry: PoolEntry;

    if (poolStack && poolStack.length > 0) {
      entry = poolStack.pop()!;
      // Reset the instance
      this.resetSpine(entry.spine, config);
    } else {
      // Create new Spine instance from shared SkeletonData
      const spine = new Spine(skelData);
      entry = { spine, skeletonKey };

      // Apply skin if specified
      if (config.defaultSkin) {
        try {
          spine.skeleton.setSkinByName(config.defaultSkin);
          spine.skeleton.setSlotsToSetupPose();
        } catch {
          // Skin not found, use default
        }
      }
    }

    // Apply time scale
    const symbolTimeScale = config.timeScale ?? 1.0;
    entry.spine.state.timeScale = symbolTimeScale * this.globalTimeScale;

    // Track
    this.activeSpines.set(cellKey, entry);
    this.cellSymbolIds.set(cellKey, symbolId);

    return entry.spine;
  }

  /**
   * Release a Spine instance back to the pool.
   */
  releaseSpine(cellKey: string): void {
    const entry = this.activeSpines.get(cellKey);
    if (!entry) return;

    this.activeSpines.delete(cellKey);
    this.cellSymbolIds.delete(cellKey);

    // Stop animations and reset
    entry.spine.state.clearTracks();
    entry.spine.skeleton.setToSetupPose();
    entry.spine.autoUpdate = true;

    // Return to pool (max 50 per skeleton key)
    const poolStack = this.pool.get(entry.skeletonKey) ?? [];
    if (poolStack.length < 50) {
      poolStack.push(entry);
      this.pool.set(entry.skeletonKey, poolStack);
    } else {
      entry.spine.destroy();
    }
  }

  // ── Animation Control ──────────────────────────────────────────

  /**
   * Trigger an animation on a cell's Spine instance.
   * Resolves the animation name from the symbol's SymbolSpineConfig.animations mapping.
   */
  playAnimation(
    cellKey: string,
    trigger: SymbolAnimationTrigger,
    loop?: boolean,
  ): void {
    const entry = this.activeSpines.get(cellKey);
    if (!entry) return;

    const symbolId = this.cellSymbolIds.get(cellKey);
    if (!symbolId) return;

    const sym = this.symbolConfigs.get(symbolId);
    if (!sym?.spineConfig) return;

    // Resolve animation name from trigger
    const animName = sym.spineConfig.animations[trigger];
    if (!animName) return;

    // Determine if this should loop
    const shouldLoop = loop ?? (trigger === 'idle' || trigger === 'nearwin' || trigger === 'highlight');

    try {
      entry.spine.state.setAnimation(0, animName, shouldLoop);
    } catch (err) {
      console.warn(
        `[SpineSymbolManager] Animation "${animName}" not found for trigger "${trigger}" on symbol "${symbolId}"`,
        err,
      );
    }
  }

  /**
   * Stop animation on a specific cell's Spine instance.
   */
  stopAnimation(cellKey: string): void {
    const entry = this.activeSpines.get(cellKey);
    if (!entry) return;

    entry.spine.state.clearTracks();
    entry.spine.skeleton.setToSetupPose();
  }

  /**
   * Stop all running Spine animations (e.g., when entering reel spin).
   */
  stopAllAnimations(): void {
    for (const [, entry] of this.activeSpines) {
      entry.spine.state.clearTracks();
      entry.spine.skeleton.setToSetupPose();
    }
  }

  /**
   * Trigger idle animations on wild/scatter symbols in the grid.
   */
  triggerIdleAnimations(): void {
    for (const [cellKey, symbolId] of this.cellSymbolIds) {
      const sym = this.symbolConfigs.get(symbolId);
      if (!sym?.spineConfig) continue;

      const category = sym.category;
      if (category === 'wild' || category === 'scatter') {
        this.playAnimation(cellKey, 'idle', true);
      }
    }
  }

  // ── Global Controls ────────────────────────────────────────────

  setGlobalTimeScale(scale: number): void {
    this.globalTimeScale = scale;
    // Update all active instances
    for (const [cellKey, entry] of this.activeSpines) {
      const symbolId = this.cellSymbolIds.get(cellKey);
      const sym = symbolId ? this.symbolConfigs.get(symbolId) : null;
      const symScale = sym?.spineConfig?.timeScale ?? 1.0;
      entry.spine.state.timeScale = symScale * this.globalTimeScale;
    }
  }

  // ── Cleanup ────────────────────────────────────────────────────

  /**
   * Release all active spines back to the pool.
   */
  releaseAll(): void {
    const keys = Array.from(this.activeSpines.keys());
    for (const key of keys) {
      this.releaseSpine(key);
    }
  }

  /**
   * Full cleanup — destroy all instances and clear caches.
   * Call when exiting play mode.
   */
  dispose(): void {
    this.disposed = true;

    // Destroy active spines
    for (const [, entry] of this.activeSpines) {
      entry.spine.destroy();
    }
    this.activeSpines.clear();
    this.cellSymbolIds.clear();

    // Destroy pooled spines
    for (const [, stack] of this.pool) {
      for (const entry of stack) {
        entry.spine.destroy();
      }
    }
    this.pool.clear();

    // Note: SkeletonData cache entries are ref-counted by SpineOffscreenRenderer
    // We don't need to release them here — they'll be cleaned up when the
    // SpineOffscreenRenderer's own cache eviction runs.
    this.skeletonDataCache.clear();
    this.symbolConfigs.clear();
  }

  // ── Helpers ────────────────────────────────────────────────────

  /** Check if a spine instance is active for a cell */
  hasSpine(cellKey: string): boolean {
    return this.activeSpines.has(cellKey);
  }

  /** Get the active Spine instance for a cell (if any) */
  getSpine(cellKey: string): Spine | null {
    return this.activeSpines.get(cellKey)?.spine ?? null;
  }

  private resetSpine(spine: Spine, config: SymbolSpineConfig): void {
    spine.state.clearTracks();
    spine.skeleton.setToSetupPose();
    spine.position.set(0, 0);
    spine.scale.set(1);
    spine.alpha = 1;
    spine.rotation = 0;

    // Apply skin
    if (config.defaultSkin) {
      try {
        spine.skeleton.setSkinByName(config.defaultSkin);
        spine.skeleton.setSlotsToSetupPose();
      } catch {
        // Skin not found
      }
    }
  }
}
